import { useRef, type ReactElement } from 'react';
import {
  Button as AriaButton,
  Menu,
  MenuItem,
  MenuTrigger,
  Popover,
  type Selection,
} from 'react-aria-components';
import { InlineLinkButton } from './Button';
import { cx } from './cx';
import { formatCount } from './format';
import { Icon } from './Icon';
import s from './FilterBar.module.css';

export interface FilterOption<V extends string = string> {
  value: V;
  label: string;
  /** How many rows it would show, from the whole list; left out, no count. */
  count?: number;
}

export interface FilterBarFilter<F extends string = string> {
  id: F;
  /** Its name on the button and the chip: "Claim". */
  name: string;
  value: string;
  /** The value that filters nothing out ("Anyone"); any other value shows as a chip. */
  defaultValue: string;
  options: readonly FilterOption[];
}

export interface FilterBarProps<F extends string = string> {
  /** Names the group of filters for a screen reader: "Filter the queue". */
  label: string;
  /** The filters that mean something on this view, in order. */
  filters: readonly FilterBarFilter<F>[];
  onChange: (id: F, value: string) => void;
  onClearFilters: () => void;
  /** Rows left after the filters, and in the whole list. */
  shown: number;
  total: number;
  /** The sort in force, in words: "Submitted, oldest first" (`sortLabel`). */
  sortWords: string;
  /** The sort is the table's own; otherwise the line offers to go back to it. */
  sortIsDefault: boolean;
  /** The table's own order, in words: "oldest first" ("Back to oldest first"). */
  defaultSortLabel: string;
  onResetSort: () => void;
}

/** "Showing 2 of 14", or "Showing all 14" when nothing is filtered out. */
function countWords(shown: number, total: number): string {
  if (total === 0) return 'Nothing to show';
  if (shown >= total) return total === 1 ? 'Showing 1' : `Showing all ${formatCount(total)}`;
  return `Showing ${formatCount(shown)} of ${formatCount(total)}`;
}

/** The option a value names, else the default's: a value never shows unless it's on the list. */
function chosen(filter: FilterBarFilter): FilterOption | undefined {
  return (
    filter.options.find((o) => o.value === filter.value) ??
    filter.options.find((o) => o.value === filter.defaultValue)
  );
}

/** A filter's menu, named by its words ("Claim: Anyone"); once set, a navy chip with a clear. */
function Filter<F extends string>({
  filter,
  onChange,
}: {
  filter: FilterBarFilter<F>;
  onChange: (id: F, value: string) => void;
}): ReactElement {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const option = chosen(filter);
  const words = option?.label ?? '';
  const set = option !== undefined && option.value !== filter.defaultValue;
  return (
    <div className={cx(s.filter, set && s.set)}>
      <MenuTrigger>
        {/* Named by the words it shows, in order, with a real space between them so they never
            run together (label in name, APP-144); the colon is a pause for a screen reader. */}
        <AriaButton
          ref={buttonRef}
          className={s.menuButton}
          aria-label={`${filter.name}: ${words}`}
        >
          <span className={s.name}>{filter.name}</span> <span className={s.value}>{words}</span>
          <Icon name="chevronDown" size={16} className={s.caret} />
        </AriaButton>
        <Popover className={s.popover} placement="bottom start">
          {/* Named by its button ("Claim: Anyone"), as every menu in a MenuTrigger is. */}
          <Menu
            className={s.menu}
            selectionMode="single"
            disallowEmptySelection
            selectedKeys={option === undefined ? [] : [option.value]}
            onSelectionChange={(keys: Selection) => {
              if (keys === 'all') return;
              const [key] = [...keys];
              if (key !== undefined) onChange(filter.id, String(key));
            }}
          >
            {filter.options.map((o) => (
              <MenuItem
                key={o.value}
                id={o.value}
                textValue={o.label}
                className={s.option}
                // Heard as "Claimed by me, 2": the words shown, in order, with a pause.
                {...(o.count !== undefined && {
                  'aria-label': `${o.label}, ${formatCount(o.count)}`,
                })}
              >
                {({ isSelected }) => (
                  <>
                    <span className={s.tick}>{isSelected && <Icon name="check" size={16} />}</span>
                    <span className={s.optionLabel}>{o.label}</span>
                    {o.count !== undefined && (
                      <>
                        {' '}
                        <span className={s.count}>{formatCount(o.count)}</span>
                      </>
                    )}
                  </>
                )}
              </MenuItem>
            ))}
          </Menu>
        </Popover>
      </MenuTrigger>
      {set && (
        <AriaButton
          className={s.clear}
          aria-label={`Clear ${filter.name}: ${words}`}
          onPress={() => {
            // The chip goes; focus stays on the filter it was.
            buttonRef.current?.focus();
            onChange(filter.id, filter.defaultValue);
          }}
        >
          <Icon name="close" size={16} />
        </AriaButton>
      )}
    </div>
  );
}

/**
 * The bar above a work table (APP-149 D4): one menu per filter, a set filter as a navy chip with
 * a clear button, and a status line under it that says what's shown and how it's sorted, read
 * politely as it changes. It offers "Back to oldest first" when the sort isn't the table's own and
 * "Clear filters" when a filter is set. The page filters and sorts the whole list, then pages it.
 */
export function FilterBar<F extends string = string>({
  label,
  filters,
  onChange,
  onClearFilters,
  shown,
  total,
  sortWords,
  sortIsDefault,
  defaultSortLabel,
  onResetSort,
}: FilterBarProps<F>): ReactElement {
  const groupRef = useRef<HTMLDivElement | null>(null);
  const lineRef = useRef<HTMLDivElement | null>(null);
  const anySet = filters.some((f) => {
    const option = chosen(f);
    return option !== undefined && option.value !== f.defaultValue;
  });
  /**
   * A link goes once it's done, so focus moves first: to "Clear filters" if it stays, else to the
   * first filter, else to the line itself.
   */
  const refocus = (stays: string | null): void => {
    const line = lineRef.current;
    const next =
      (stays === null ? null : line?.querySelector<HTMLElement>(`[data-link="${stays}"]`)) ??
      groupRef.current?.querySelector<HTMLElement>('button') ??
      line;
    next?.focus();
  };
  return (
    <div className={s.bar}>
      {filters.length > 0 && (
        <div ref={groupRef} role="group" aria-label={label} className={s.filters}>
          <span className={s.lead}>
            <Icon name="filter" size={16} />
          </span>
          {filters.map((filter) => (
            <Filter key={filter.id} filter={filter} onChange={onChange} />
          ))}
        </div>
      )}
      <div ref={lineRef} className={s.line} tabIndex={-1}>
        <p role="status" className={s.status}>
          <span>{countWords(shown, total)}</span>
          {/* APP-254, as Home's line (APP-248): the dot is drawn by CSS, so the line's text (what's
              read, copied or found) is "Showing all 14. Sorted by …", never "14 · . Sorted". */}
          <span className={s.sep} aria-hidden="true" />
          <span className="visually-hidden">. </span>
          <span>Sorted by {sortWords}</span>
        </p>
        {!sortIsDefault && (
          <InlineLinkButton
            onPress={() => {
              refocus(anySet ? 'clear' : null);
              onResetSort();
            }}
          >
            Back to {defaultSortLabel}
          </InlineLinkButton>
        )}
        {anySet && (
          <InlineLinkButton
            data-link="clear"
            onPress={() => {
              refocus(null);
              onClearFilters();
            }}
          >
            Clear filters
          </InlineLinkButton>
        )}
      </div>
    </div>
  );
}
